import { INTENT_TYPE } from '../../constants';
import { stripLeadingPhrase, stripTrailingPhrase } from '../features/cues';
import { hasOperandEvidence } from '../features/operand-evidence';
import {
	BENEFICIARY_FRAMES,
	COORDINATORS,
	REQUEST_PREFIXES,
	REQUEST_VERBS,
} from '../pack/lexicons';
import { type ConvergenceRule, type FeatureContext } from '../types';

/**
 * `C-REQUEST` (943) and `C-REQUEST-BARE` (942) — request verbs, split by framing.
 *
 * These two rules share one predicate and differ only in whether a frame is present, and
 * that split is the whole point. `review`, `check`, `fix`, and `write` collide heavily
 * with real page and issue titles — the Jira corpus showed exactly that — so the verb
 * alone is not evidence of a request.
 *
 * A **frame** makes it one: either an approved request prefix (`can you review my PR`) or
 * an exact trailing beneficiary phrase (`review my PR for me`). Framed requests are high
 * confidence. Bare ones are a separate rule reporting a separate pattern, so a caller can
 * tell the two apart — the design is explicit that the bare variant's launch state must
 * not be coupled to the framed one.
 *
 * A coordinator immediately after the verb blocks both: `review and approve the doc` is a
 * compound instruction, and the browser should not pick which of two verbs to route on.
 */
export const parseRequest = (features: FeatureContext): { isFramed: boolean } | null => {
	const qMatch = features.qMatch;

	const trailingFrame = stripTrailingPhrase(qMatch, BENEFICIARY_FRAMES);
	const body = trailingFrame ? trailingFrame.rest : qMatch;
	const prefix = stripLeadingPhrase(body, REQUEST_PREFIXES);
	const cue = stripLeadingPhrase(prefix ? prefix.rest : body, REQUEST_VERBS);

	if (!cue || stripLeadingPhrase(cue.rest, COORDINATORS) || !hasOperandEvidence(cue.rest)) {
		return null;
	}

	return { isFramed: prefix !== null || trailingFrame !== null };
};

export const requestRule: ConvergenceRule = {
	type: INTENT_TYPE.CHAT,
	pattern: 'REQUEST_FRAME',
	matches: (features) => parseRequest(features)?.isFramed === true,
};

export const requestBareRule: ConvergenceRule = {
	type: INTENT_TYPE.CHAT,
	pattern: 'REQUEST_BARE',
	matches: (features) => parseRequest(features)?.isFramed === false,
};
